/**
 * Copyright (c) 2026
 *
 * Styled `<img>` with optional error fallback and optional context-menu suppression.
 */

import { forwardRef, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

export interface ImageProps extends Omit<
  React.ComponentPropsWithoutRef<'img'>,
  'onContextMenu' | 'onError' | 'onLoad'
> {
  /** Loaded when the primary `src` fails. Applied at most once per `src` to avoid reload loops. */
  fallbackSrc?: string
  /** When true (default), `contextmenu` is canceled after any custom handler runs. */
  disableContextMenu?: boolean
  onContextMenu?: React.MouseEventHandler<HTMLImageElement>
  onError?: React.ReactEventHandler<HTMLImageElement>
  onLoad?: React.ReactEventHandler<HTMLImageElement>
}

const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    className,
    src,
    fallbackSrc,
    disableContextMenu = true,
    draggable = false,
    onContextMenu,
    onError,
    onLoad,
    ...props
  },
  ref
) {
  const [displaySrc, setDisplaySrc] = useState(src)
  const fallbackConsumedRef = useRef(false)

  useEffect(() => {
    setDisplaySrc(src)
    fallbackConsumedRef.current = false
  }, [src])

  return (
    <img
      ref={ref}
      src={displaySrc}
      draggable={draggable}
      className={cn('h-auto max-w-full select-none', className)}
      data-slot="image"
      onContextMenu={(event) => {
        onContextMenu?.(event)
        if (disableContextMenu) {
          event.preventDefault()
        }
      }}
      onLoad={onLoad}
      onError={(event) => {
        onError?.(event)
        if (fallbackSrc !== undefined && !fallbackConsumedRef.current) {
          fallbackConsumedRef.current = true
          setDisplaySrc(fallbackSrc)
        }
      }}
      {...props}
    />
  )
})

export type ImageRef = HTMLImageElement

export { Image }
